import { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Checkbox } from "../../ui/checkbox";
import {
    Plus,
    Edit3,
    Trash2,
    Monitor,
    Laptop,
    Smartphone,
    X,
    Hash,
    Building2,
    Search,
    ChevronLeft,
    ChevronRight,
    Group,
    Clock,
    Shield,
    LoaderCircle
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { myLocalStorage } from "../../StorageHelper";
import { addGroupDetails, deleteDeviceGroup, editGroupDetails, fetchComputerForCategory, fetchEndpointGroups } from "./Api/endpointgroupapi";
import { transformApiToDevice } from "../Endpoints/helper";
import { Device } from "../interfaces/EndpointInterfaces";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { PolicyProfiles } from "../PolicyProfiles/interface";
import { getProfiles } from "../PolicyProfiles/Api/policyprofileapi";
import { formatDate } from "../formatTimeAgo";
import { Skeleton } from "../../ui/skeleton";
import EmptyStateCard from "../EmptyStateCard";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";

import EndpointActionButton from "../Endpoints/EndpointActionButton";
import "./EndpointGroups.css";
import "../ManagementDialog.css";



interface DevicePattern {
    id: string;
    pattern: string;
    includedDevices: Device[];
    excludedDevices: Device[];
}

export default function EndpointGroupDetails() {
    const [groups, setGroups] = useState<EndpointGroups[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<string | null>("1");
    const [activeTab, setActiveTab] = useState<"windows" | "linux" | "macos">("windows");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [groupsSearch, setGroupsSearch] = useState("");
    const [devicesSearch, setDevicesSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [groupDevices, setGroupDevices] = useState<Device[]>([]);
    const [devices, setDevices] = useState<Device[] | []>([])
    const [liveMatches, setLiveMatches] = useState<any[]>([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedGroupDetails, setSelectedGroupDetails] = useState<EndpointGroups>();
    const DESCRIPTION_MAX_LENGTH = 25;


    const groupsPerPage = 6;
    const getDefaultFormData = () => ({
        name: "",
        priority: 10,
        profile: "",
        criteria: "install_tags" as "install_tags" | "organization_unit" | "device_pattern",
        tags: [] as string[],
        organizationUnit: "",
        devicePatterns: [] as DevicePattern[],
        patternInput: "",
        tagInput: "",
        isEdit: false
    });
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";

    const [formData, setFormData] = useState(getDefaultFormData());
    const [selectedPattern, setSelectedPattern] = useState<string>("all");

    const [profiles, setProfiles] = useState<PolicyProfiles[]>([]);

    const [loading, setLoading] = useState(false);

    const { toast } = useToast();

    const hasDevicePattern = formData.devicePatterns.length > 0 || formData.patternInput.trim() !== "";
    const hasTags = formData.tags.length > 0 || formData.tagInput.trim() !== "";
    const hasOrganizationUnit = formData.organizationUnit.trim() !== "";

    const disableInstallTags = (hasDevicePattern || hasOrganizationUnit) && formData.criteria !== "install_tags";
    const disableOrganizationUnit = (hasDevicePattern || hasTags) && formData.criteria !== "organization_unit";
    const disableDevicePattern = (hasTags || hasOrganizationUnit) && formData.criteria !== "device_pattern";

    const filteredGroups = useMemo(() => {
        return groups
            .filter(group => group.operationSystem === activeTab.toUpperCase())
            .filter(group =>
                groupsSearch === "" ||
                group.name.toLowerCase().includes(groupsSearch.toLowerCase()) ||
                group.profileName.toLowerCase().includes(groupsSearch.toLowerCase())
            );
    }, [groups, activeTab, groupsSearch]);

    const totalPages = Math.ceil(filteredGroups.length / groupsPerPage);
    const startIndex = (currentPage - 1) * groupsPerPage;
    const paginatedGroups = filteredGroups.slice(startIndex, startIndex + groupsPerPage);

    useMemo(() => {
        setCurrentPage(1);
    }, [activeTab, groupsSearch]);

    const selectedGroupDevices = useMemo(() => {


        return devicesSearch === ""
            ? groupDevices
            : groupDevices.filter(device =>
                device.name.toLowerCase().includes(devicesSearch.toLowerCase()) ||
                device.os.toLowerCase().includes(devicesSearch.toLowerCase())
            );
    }, [groupDevices, selectedGroup, devicesSearch]);

    const getMatchingDevices = (pattern: string) => {
        return groupDevices.filter(device =>
            device.name.toLowerCase().includes(pattern.toLowerCase())
        );
    };



    const handleCreateGroup = async () => {
        const isDevicePattern = formData.criteria === "device_pattern";
        const isInstallTags = formData.criteria === "install_tags";
        const isOrganizationUnit = formData.criteria === "organization_unit";
        let osType = (activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS") ? "MACOS" : activeTab.toUpperCase();
        let payload = {
            name: formData.name,
            tenantName,
            profileId: -1,
            profileName: formData.profile,
            os: osType,
            reputationScore: formData.priority,
            searchPatterns: isDevicePattern
                ? formData.devicePatterns.map(p => ({
                    id: p.id,
                    text: p.pattern
                }))
                : [],
            manualExclusions: isDevicePattern
                ? formData.devicePatterns.flatMap(p =>
                    p.excludedDevices.map(d => ({
                        id: Number(d.id),
                        name: d.name
                    }))
                )
                : [],
            installTags: isInstallTags ? (formData.tags || []) : [],
            organizationUnit: isOrganizationUnit ? (formData.organizationUnit || "") : ""
        };

        if (formData.isEdit) {
            let profileId = profiles.find(
                p => p.name?.toLowerCase() === formData.profile?.toLowerCase() && p.os?.toUpperCase() === activeTab.toUpperCase()
            )?.id ?? selectedGroupDetails?.profileId ?? -1;
            let osType = (activeTab.toUpperCase() === "MAC" || activeTab.toUpperCase() === "MACOS") ? "MACOS" : activeTab.toUpperCase();

            let editpayload = {
                uuid: selectedGroupDetails?.uuid,
                name: selectedGroupDetails?.name,
                newName: formData.name,
                tenantName,
                profileId: profileId,
                profileName: formData.profile,
                os: osType,
                reputationScore: formData.priority,

                oldSearchPatterns: selectedGroupDetails?.searchPatterns || [],

                newSearchPatterns: isDevicePattern
                    ? formData.devicePatterns.map(p => ({
                        id: p.id,
                        text: p.pattern
                    }))
                    : [],

                manualExclusions: isDevicePattern
                    ? formData.devicePatterns.flatMap(p =>
                        p.excludedDevices.map(d => ({
                            id: Number(d.id),
                            name: d.name
                        }))
                    )
                    : [],

                installTags: isInstallTags ? (formData.tags || []) : [],

                ouId: isOrganizationUnit ? (selectedGroupDetails?.organizationalUnitId ?? null) : null,
                organizationUnit: isOrganizationUnit ? (formData.organizationUnit || "") : "",
                endpointIdList: selectedGroupDetails?.endpointsIdList || []
            };

            await editGroupDetails(editpayload);

            setIsCreateDialogOpen(false);
            getDeviceList();
            getEndpointGroups();
            toast({
                title: "Group Updated",
                description: `Endpoint group "${formData.name}" has been updated. Resources might reflect in few minutes.`,
            });

        } else {
            const selectedProfile = profiles.find(
                p => p.name?.toLowerCase() === formData.profile?.toLowerCase() && p.os?.toUpperCase() === activeTab.toUpperCase()
            );

            const profileId = selectedProfile?.id || -1;
            payload = { ...payload, profileId }
            let response = await addGroupDetails(payload);
            if (response) {
                setIsCreateDialogOpen(false);
                getDeviceList();
                getEndpointGroups();
                toast({
                    title: "Group Created",
                    description: `Endpoint group "${formData.name}" has been created. Resources might reflect in few minutes.`,
                });
            }
        }


    };

    const openEditGroup = (group: EndpointGroups) => {
        getComputerGroup(group.name);
        setSelectedGroupDetails(group);
        const updatedFormData = getDefaultEditFormData(group);
        setFormData(updatedFormData);
        setIsCreateDialogOpen(true);

    }

    const getDefaultEditFormData = (data?: EndpointGroups) => ({
        name: data?.name || "",
        priority: data?.reputationScore || 10,
        profile:
            data?.profileName ||
            (profiles.filter((p) => p.name.startsWith(activeTab))[0].name || ""),
        criteria: data
            ? data.searchPatterns?.length > 0
                ? "device_pattern"
                : data.installTags?.length > 0
                    ? "install_tags"
                    : data.organizationalUnitName
                        ? "organization_unit"
                        : "install_tags"
            : ("install_tags" as "install_tags" | "organization_unit" | "device_pattern"),
        tags: data?.installTags || [],
        organizationUnit: data?.organizationalUnitName || "",
        devicePatterns: data?.searchPatterns?.map((pattern) => {
            const excludedDevices = data.manualExclusions?.map((ex) =>
                devices.find((d) => d.id === ex.id.toString())
            ).filter(Boolean) as Device[] || [];

            const includedDevices = devices.filter((device) => {
                const regex = new RegExp(pattern.replace(/\*/g, ".*").replace(/\?/g, "."), "i");
                const matches = regex.test(device.name);
                const isExcluded = excludedDevices.some((d) => d.id === device.id);
                return matches && !isExcluded;
            });

            return {
                id: Date.now().toString() + Math.random().toString(),
                pattern,
                includedDevices,
                excludedDevices
            };
        }) || [],
        patternInput: "",
        tagInput: "",
        isEdit: true
    });

    const handleDeleteGroup = (groupId: string, groupName: string, group: EndpointGroups) => {
        setSelectedGroupDetails(group);
        setShowDeleteConfirmation(true);

    };
    const handleDelete = async (group: EndpointGroups) => {
        setGroups(groups.filter(g => g.uuid !== group.uuid));
        if (selectedGroup === group.uuid) {
            setSelectedGroup(null);
        }
        await deleteDeviceGroup(group.uuid);
        setShowDeleteConfirmation(false);
        getDeviceList();
        getEndpointGroups();
        toast({
            title: "Group Deleted",
            description: `Endpoint group "${group.name}" has been deleted. Resources might reflect in few minutes.`,
        });
    }

    const addTag = () => {
        if (formData.tagInput.trim() && !formData.tags.includes(formData.tagInput.trim())) {
            setFormData({
                ...formData,
                tags: [...formData.tags, formData.tagInput.trim()],
                tagInput: ""
            });
        }
    };

    const removeTag = (tagToRemove: string) => {
        setFormData({
            ...formData,
            tags: formData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addTag();
        }
    };

    const getMatchingPattern = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setFormData({
            ...formData,
            patternInput: value,
        });

        if (!value.trim()) {
            setLiveMatches([]);
            return;
        }

        const regexPattern = value.replace(/\*/g, ".*").replace(/\?/g, ".");
        try {
            const regex = new RegExp(regexPattern, "i");
            const matches = devices.filter((device) => regex.test(device.name) && !device.isManuallyAddedMachine);
            setLiveMatches(matches);
        } catch (error) {
            console.warn("Invalid pattern:", error);
            setLiveMatches([]);
        }
    };


    const addPattern = () => {
        if (formData.patternInput.trim()) {
            const matchingDevices = liveMatches;

            const newPattern: DevicePattern = {
                id: Date.now().toString(),
                pattern: formData.patternInput.trim(),
                includedDevices: matchingDevices,
                excludedDevices: [],
            };

            setFormData({
                ...formData,
                devicePatterns: [...formData.devicePatterns, newPattern],
                patternInput: "",
            });

            setLiveMatches([]);
        }
    };

    const removePattern = (patternId: string) => {
        const patternToRemove = formData.devicePatterns.find(p => p.id === patternId);
        setFormData({
            ...formData,
            devicePatterns: formData.devicePatterns.filter(p => p.id !== patternId)
        });
        if (selectedPattern === patternToRemove?.pattern) {
            setSelectedPattern("all");
        }
    };

    const toggleDeviceInPattern = (patternId: string, device: Device) => {
        setFormData((prev) => ({
            ...prev,
            devicePatterns: prev.devicePatterns.map((pattern) => {
                if (pattern.id === patternId) {
                    const isIncluded = pattern.includedDevices.some((d) => d.id === device.id);

                    if (isIncluded) {
                        return {
                            ...pattern,
                            includedDevices: pattern.includedDevices.filter((d) => d.id !== device.id),
                            excludedDevices: [
                                ...pattern.excludedDevices.filter((d) => d.id !== device.id),
                                device,
                            ],
                        };
                    } else {
                        return {
                            ...pattern,
                            includedDevices: [
                                ...pattern.includedDevices.filter((d) => d.id !== device.id),
                                device,
                            ],
                            excludedDevices: pattern.excludedDevices.filter((d) => d.id !== device.id),
                        };
                    }
                }
                return pattern;
            }),
        }));
    };


    const includeAllDevicesInPattern = (patternId: string) => {
        const pattern = formData.devicePatterns.find((p) => p.id === patternId);
        if (pattern) {
            const matchingDevices = getMatchingDevices(pattern.pattern);

            setFormData((prev) => ({
                ...prev,
                devicePatterns: prev.devicePatterns.map((p) =>
                    p.id === patternId
                        ? {
                            ...p,
                            includedDevices: matchingDevices,
                            excludedDevices: p.excludedDevices.filter(
                                (d) => !matchingDevices.some((m) => m.id === d.id)
                            ),
                        }
                        : p
                ),
            }));
        }
    };


    const removeAllDevicesInPattern = (patternId: string) => {
        setFormData({
            ...formData,
            devicePatterns: formData.devicePatterns.map(p =>
                p.id === patternId
                    ? { ...p, includedDevices: [] }
                    : p
            )
        });
    };

    const getOSIcon = (osType: string) => {
        switch (osType) {
            case "windows": return <FaWindows className="w-6 h-6" />;
            case "linux": return <FaLinux className="w-6 h-6 text-black dark:text-white" />;
            case "mac":
            case "macos": return <FaApple className="w-6 h-6 text-gray-700 dark:text-gray-300" />;
            default: return <FaLinux className="w-6 h-6 text-black dark:text-white" />;
        }
    };



    const getComputerGroup = async (categoryName: string) => {
        try {


            const filteredDevices = devices.filter(device =>
                (device.categories ?? []).some(
                    cat => cat.categoryName.toLowerCase() === categoryName.toLowerCase()
                )
            );

            setGroupDevices(filteredDevices as Device[]);
        } catch (error) {
            console.error("Error fetching computer group:", error);
            return [];
        }
    };

    const getEndpointGroups = async (isFirst = false) => {
        setLoading(isFirst);
        let response: EndpointGroups[] = await fetchEndpointGroups(activeTab, tenantName);
        setGroups(response);
        setLoading(false);
    }

    const getDeviceList = async () => {
        const response = await fetchComputerForCategory(activeTab, tenantName);

        const devices = transformApiToDevice(response);
        const filteredDevices = devices.filter(device => !device.isManuallyAddedMachine);

        setDevices(filteredDevices);
    }

    const fetchProfiles = async (tenantName: string) => {
        let response: PolicyProfiles[] = await getProfiles(tenantName);
        if (response) {
            setProfiles(response);
        }
    }

    const normalizeOS = (os?: string) => {
        if (!os) return '';

        const value = os.toLowerCase();

        if (value === 'macos') return 'mac';
        return value;
    };


    useEffect(() => {
        getDeviceList();
        getEndpointGroups(true);

    }, [activeTab])
    useEffect(() => {
        if (tenantName)
            fetchProfiles(tenantName);
    }, [tenantName])


    return (
        <div className="endpoint-groups">
            <Tabs
                value={activeTab}
                onValueChange={(value) => {
                    setActiveTab(value as any);
                    setFormData(getDefaultFormData());
                    setIsCreateDialogOpen(false);
                    setSelectedPattern("all");
                    setSelectedGroup(null);
                    setGroupsSearch("");
                    setDevicesSearch("");
                    setCurrentPage(1);
                    setGroupDevices([]);
                }}
                className="endpoint-groups-layout"
            >
                <div className="endpoint-groups-toolbar">
                    <TabsList unstyled className="endpoint-groups-tabs" aria-label="Operating system">
                        <TabsTrigger unstyled value="windows" className="endpoint-groups-tab" data-testid="tab-windows">
                            <FaWindows aria-hidden="true" />
                            Windows
                        </TabsTrigger>

                        <TabsTrigger unstyled value="macos" className="endpoint-groups-tab" data-testid="tab-mac">
                            <FaApple aria-hidden="true" />
                            Mac
                        </TabsTrigger>
                        <TabsTrigger unstyled value="linux" className="endpoint-groups-tab" data-testid="tab-linux">
                            <FaLinux aria-hidden="true" />
                            Linux
                        </TabsTrigger>
                    </TabsList>
                    <EndpointActionButton
                        onClick={() => {
                            setFormData(getDefaultFormData());
                            setIsCreateDialogOpen(true);
                        }}
                        className="endpoint-groups-create"
                        data-testid="button-create-group"
                    >
                        <Plus className="w-4 h-4" />
                        Create new group
                    </EndpointActionButton>
                </div>

                <TabsContent value={activeTab} className="endpoint-groups-content">
                    <div className="endpoint-groups-grid">
                        <Card className="endpoint-groups-panel">
                            <CardHeader className="endpoint-groups-panel-header">
                                <div className="endpoint-groups-heading">
                                    <span className="endpoint-groups-heading-icon">{getOSIcon(activeTab)}</span>
                                    <CardTitle className="endpoint-groups-title">Endpoint Groups</CardTitle>
                                    <span className="endpoint-groups-count">{filteredGroups.length}</span>
                                </div>
                                <div className="endpoint-groups-search">
                                    <Search aria-hidden="true" />
                                    <Input
                                        placeholder="Search groups..."
                                        value={groupsSearch}
                                        onChange={(e) => setGroupsSearch(e.target.value)}
                                        aria-label="Search groups"
                                        data-testid="input-search-groups"
                                    />
                                </div>
                            </CardHeader>
                            <CardContent className="endpoint-groups-panel-content">
                                {loading ? (
                                    <div
                                        className="endpoint-groups-list"
                                        role="status"
                                        aria-label="Loading endpoint groups"
                                    >
                                        {Array.from({ length: 4 }).map((_, index) => (
                                            <div
                                                key={index}
                                                className="endpoint-group-loading-entry"
                                                aria-hidden="true"
                                            >
                                                <Skeleton className="endpoint-group-loading-icon" />
                                                <div className="endpoint-group-loading-copy">
                                                    <Skeleton className="endpoint-group-loading-name" />
                                                    <Skeleton className="endpoint-group-loading-meta" />
                                                    <Skeleton className="endpoint-group-loading-meta" />
                                                </div>
                                                <Skeleton className="endpoint-group-loading-action" />
                                                <Skeleton className="endpoint-group-loading-action" />
                                            </div>
                                        ))}
                                    </div>
                                ) : paginatedGroups.length > 0 ? (
                                    <div className="endpoint-groups-list">
                                        {paginatedGroups.map((group) => (
                                            <Card
                                                key={group.uuid}
                                                className="endpoint-group-entry"
                                                data-selected={selectedGroup === group.uuid}
                                                onClick={() => {
                                                    setSelectedGroup(group.uuid);
                                                    getComputerGroup(group.name);
                                                }}
                                                data-testid={`card-group-${group.uuid}`}
                                            >
                                                <CardContent className="endpoint-group-entry-content">
                                                    <div className="endpoint-group-row">
                                                        <div className="endpoint-group-identity">
                                                            <div className="endpoint-group-priority" title="Priority">
                                                                {group.reputationScore}
                                                            </div>
                                                            <div className="endpoint-group-copy">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-foreground text-sm">
                                                                        {group.name}
                                                                    </span>
                                                                </div>
                                                                <div className="endpoint-group-profile">
                                                                    <Shield aria-hidden="true" />
                                                                    <span>{group.profileName}</span>
                                                                </div>
                                                                <div className="endpoint-group-date">
                                                                    <Clock aria-hidden="true" />
                                                                    <span>{formatDate(group?.createdAt)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="endpoint-group-actions">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="endpoint-group-action"
                                                                aria-label={`Edit ${group.name}`}
                                                                data-testid={`button-edit-${group.uuid}`}
                                                                onClick={() => openEditGroup(group)}
                                                            >
                                                                <Edit3 className="w-3 h-3" />
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteGroup(group.uuid, group.name, group);
                                                                }}
                                                                className="endpoint-group-action text-destructive"
                                                                aria-label={`Delete ${group.name}`}
                                                                data-testid={`button-delete-${group.uuid}`}
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyStateCard
                                        icon={Group}
                                        title={`No endpoint groups for ${activeTab.toUpperCase()}`}
                                        description="Create your first endpoint group to organize devices by tags or patterns."
                                        embedded
                                        className="endpoint-groups-empty"
                                    />
                                )}
                            </CardContent>
                            {!loading && paginatedGroups.length > 0 && totalPages > 1 && (
                                <div className="endpoint-groups-pagination">
                                    <div className="text-sm text-muted-foreground">
                                        Showing {startIndex + 1}-
                                        {Math.min(startIndex + groupsPerPage, filteredGroups.length)} of{" "}
                                        {filteredGroups.length} groups
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            data-testid="button-prev-page"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-sm font-medium">
                                            {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            data-testid="button-next-page"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card className="endpoint-groups-panel">
                            <CardHeader className="endpoint-groups-panel-header">
                                <div className="endpoint-groups-heading">
                                    <span className="endpoint-groups-heading-icon">
                                        <Monitor aria-hidden="true" />
                                    </span>
                                    <CardTitle className="endpoint-groups-title">Devices</CardTitle>
                                    <span className="endpoint-groups-count">{selectedGroupDevices.length}</span>
                                </div>
                                <div className="endpoint-groups-search">
                                    <Search aria-hidden="true" />
                                    <Input
                                        placeholder="Search devices..."
                                        value={devicesSearch}
                                        onChange={(e) => setDevicesSearch(e.target.value)}
                                        aria-label="Search devices"
                                        data-testid="input-search-devices"
                                    />
                                </div>
                            </CardHeader>
                            <CardContent className="endpoint-groups-panel-content">
                                {loading ? (
                                    <div className="endpoint-groups-list" role="status" aria-label="Loading devices">
                                        {Array.from({ length: 3 }).map((_, index) => (
                                            <div
                                                key={index}
                                                className="endpoint-group-loading-entry endpoint-group-loading-device"
                                                aria-hidden="true"
                                            >
                                                <Skeleton className="endpoint-group-loading-icon" />
                                                <div className="endpoint-group-loading-copy">
                                                    <Skeleton className="endpoint-group-loading-name" />
                                                    <Skeleton className="endpoint-group-loading-meta" />
                                                </div>
                                                <Skeleton className="endpoint-group-loading-action" />
                                            </div>
                                        ))}
                                    </div>
                                ) : selectedGroupDevices.length > 0 ? (
                                    <div className="endpoint-groups-list">
                                        {selectedGroupDevices.map((device, index) => (
                                            <Card
                                                key={device.id}
                                                className="endpoint-group-device"
                                                data-testid={`card-device-${device.id}`}
                                            >
                                                <CardContent className="endpoint-group-entry-content">
                                                    <div className="endpoint-group-row">
                                                        <div className="endpoint-group-identity">
                                                            <div className="endpoint-group-priority">{index + 1}</div>
                                                            <div className="endpoint-group-copy">
                                                                <div className="flex items-center gap-2">
                                                                    <Monitor className="w-3 h-3 text-muted-foreground" />
                                                                    <span className="font-medium text-foreground text-sm">
                                                                        {device.name}
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    {device.os}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="endpoint-group-device-status">
                                                            <span className="endpoint-group-online-dot" />
                                                            <Badge variant="outline" className="endpoint-group-online">
                                                                Online
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyStateCard
                                        icon={Monitor}
                                        title={selectedGroup ? "No devices in this group" : "No group selected"}
                                        description={
                                            selectedGroup
                                                ? "No devices match your search in this endpoint group."
                                                : "Select an endpoint group from the list to view its devices."
                                        }
                                        compact
                                        embedded
                                        className="endpoint-groups-empty"
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="management-dialog endpoint-group-dialog">
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon">
                                <Group aria-hidden="true" />
                            </span>
                            <div>
                                <DialogTitle>
                                    {formData.isEdit ? "Edit Endpoint Group" : "Create Endpoint Group"}
                                </DialogTitle>
                                <DialogDescription>
                                    {formData.isEdit ? "Update" : "Configure"} the profile and matching criteria for{" "}
                                    {activeTab === "macos" ? "Mac" : activeTab === "windows" ? "Windows" : "Linux"}{" "}
                                    devices.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="management-dialog-body">
                        <section className="management-form-section">
                            <h3 className="management-section-title">
                                <Shield aria-hidden="true" /> Group settings
                            </h3>
                            <div className="endpoint-group-fields">
                                <div className="management-form-field">
                                    <Label htmlFor="group-name" className="text-xs font-medium text-muted-foreground">
                                        Group Name *
                                    </Label>
                                    <Input
                                        id="group-name"
                                        value={formData.name}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                name: e.target.value.slice(0, DESCRIPTION_MAX_LENGTH),
                                            })
                                        }
                                        placeholder="e.g., desktop-machines"
                                        className="h-8"
                                        data-testid="input-group-name"
                                    />
                                    <p className="text-[10px] text-muted-foreground text-right">
                                        {formData.name.length}/{DESCRIPTION_MAX_LENGTH}
                                    </p>
                                </div>
                                <div className="management-form-field">
                                    <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                                        Priority *
                                    </Label>
                                    <Input
                                        id="priority"
                                        type="number"
                                        min={1}
                                        max={100}
                                        step={1}
                                        className="h-8"
                                        value={formData.priority.toString()}
                                        onChange={(e) => {
                                            const value = Number(e.target.value);
                                            setFormData({
                                                ...formData,
                                                priority: Number.isFinite(value)
                                                    ? Math.min(Math.max(value, 1), 100)
                                                    : 1,
                                            });
                                        }}
                                        placeholder="10"
                                        data-testid="input-priority"
                                    />
                                </div>
                            </div>

                            <div className="management-form-field">
                                <Label htmlFor="group-profile">Select Profile *</Label>
                                <Select
                                    value={formData.profile}
                                    onValueChange={(value) => setFormData({ ...formData, profile: value })}
                                >
                                    <SelectTrigger id="group-profile" data-testid="select-profile">
                                        <SelectValue placeholder={`Select profile for ${activeTab.toLowerCase()}`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {profiles
                                            .filter((profile) => normalizeOS(profile?.os) === normalizeOS(activeTab))
                                            .map((profile) => (
                                                <SelectItem key={profile.name} value={profile.name}>
                                                    {profile.name}
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </section>
                        <section className="management-form-section">
                            <h3 className="management-section-title">
                                <Search aria-hidden="true" /> Group criteria
                            </h3>
                            <RadioGroup
                                value={formData.criteria}
                                onValueChange={(value) => setFormData({ ...formData, criteria: value as any })}
                                className="endpoint-group-criteria"
                                aria-label="Group criteria"
                            >
                                <div className="endpoint-group-criterion">
                                    <RadioGroupItem
                                        value="install_tags"
                                        id="install_tags"
                                        data-testid="radio-install-tags"
                                        disabled={disableInstallTags}
                                    />
                                    <Label htmlFor="install_tags" className="text-sm flex items-center gap-1">
                                        <Hash className="w-3 h-3" />
                                        Install Tags
                                    </Label>
                                </div>
                                {activeTab === "windows" && (
                                    <div className="endpoint-group-criterion">
                                        <RadioGroupItem
                                            value="organization_unit"
                                            id="organization_unit"
                                            data-testid="radio-org-unit"
                                            disabled={disableOrganizationUnit}
                                        />
                                        <Label htmlFor="organization_unit" className="text-sm flex items-center gap-1">
                                            <Building2 className="w-3 h-3" />
                                            Organization Unit
                                        </Label>
                                    </div>
                                )}
                                <div className="endpoint-group-criterion">
                                    <RadioGroupItem
                                        value="device_pattern"
                                        id="device_pattern"
                                        data-testid="radio-device-pattern"
                                        disabled={disableDevicePattern}
                                    />
                                    <Label htmlFor="device_pattern" className="text-sm flex items-center gap-1">
                                        <Search className="w-3 h-3" />
                                        Device Pattern
                                    </Label>
                                </div>
                            </RadioGroup>
                            {formData.criteria === "install_tags" && (
                                <div className="management-form-field endpoint-group-criteria-content">
                                    <Label htmlFor="group-tags" className="text-sm font-medium">Tags</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            id="group-tags"
                                            value={formData.tagInput}
                                            onChange={(e) => setFormData({ ...formData, tagInput: e.target.value })}
                                            onKeyDown={handleTagInputKeyDown}
                                            placeholder="Add Tag"
                                            className="flex-1"
                                            data-testid="input-tag"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={addTag}
                                            data-testid="button-add-tag"
                                        >
                                            Add
                                        </Button>
                                    </div>
                                    {formData.tags.length > 0 && (
                                        <div className="endpoint-group-tags">
                                            {formData.tags.map((tag, index) => (
                                                <Badge key={index} variant="outline" className="endpoint-group-tag">
                                                    {tag}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeTag(tag)}
                                                        className="ml-1 h-auto p-0"
                                                        aria-label={`Remove ${tag}`}
                                                        data-testid={`button-remove-tag-${index}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "windows" && formData.criteria === "organization_unit" && (
                                <div className="management-form-field endpoint-group-criteria-content">
                                    <Label htmlFor="org-unit" className="text-sm font-medium">
                                        Organization Unit
                                    </Label>
                                    <Input
                                        id="org-unit"
                                        value={formData.organizationUnit}
                                        onChange={(e) => setFormData({ ...formData, organizationUnit: e.target.value })}
                                        placeholder="Enter organization unit"
                                        data-testid="input-org-unit"
                                    />
                                </div>
                            )}

                            {formData.criteria === "device_pattern" && (
                                <div className="endpoint-group-criteria-content space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="group-pattern" className="text-xs text-primary">Pattern</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                id="group-pattern"
                                                placeholder="Enter pattern"
                                                data-testid="input-pattern"
                                                className="border-2 border-primary/20 focus:border-primary/40"
                                                value={formData.patternInput}
                                                onChange={(e) => getMatchingPattern(e)}
                                                onKeyDown={(e) => e.key === "Enter" && addPattern()}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={addPattern}
                                                className="px-6"
                                                data-testid="button-add-pattern"
                                            >
                                                Add pattern
                                            </Button>
                                        </div>
                                    </div>
                                    {formData.devicePatterns.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.devicePatterns.map((pattern) => (
                                                <Badge
                                                    key={pattern.id}
                                                    variant={
                                                        selectedPattern === pattern.pattern ? "default" : "secondary"
                                                    }
                                                    className={`px-3 py-1 text-sm flex items-center gap-1 cursor-pointer ${
                                                        selectedPattern === pattern.pattern
                                                            ? "bg-primary text-primary-foreground"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        setSelectedPattern(
                                                            selectedPattern === pattern.pattern ? "" : pattern.pattern,
                                                        )
                                                    }
                                                    data-testid={`badge-pattern-${pattern.id}`}
                                                    data-selected={selectedPattern === pattern.pattern}
                                                >
                                                    {pattern.pattern}
                                                    <button
                                                        type="button"
                                                        aria-label={`Remove pattern ${pattern.pattern}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removePattern(pattern.id);
                                                        }}
                                                        data-testid={`button-remove-pattern-${pattern.id}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs font-medium text-muted-foreground">
                                                Matched Devices
                                            </Label>
                                            {formData.devicePatterns.length > 0 && selectedPattern && (
                                                <div className="flex gap-1">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-6 px-2 text-xs"
                                                        onClick={() => {
                                                            const pattern = formData.devicePatterns.find(
                                                                (p) => p.pattern === selectedPattern,
                                                            );
                                                            if (pattern) includeAllDevicesInPattern(pattern.id);
                                                        }}
                                                    >
                                                        Include all
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-6 px-2 text-xs"
                                                        onClick={() => {
                                                            const pattern = formData.devicePatterns.find(
                                                                (p) => p.pattern === selectedPattern,
                                                            );
                                                            if (pattern) removeAllDevicesInPattern(pattern.id);
                                                        }}
                                                    >
                                                        Remove all
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="endpoint-group-matches">
                                            <div className="endpoint-group-matches-header grid grid-cols-12 gap-2">
                                                <div className="col-span-10">Device</div>
                                                <div className="col-span-2">Included</div>
                                            </div>

                                            <div className="endpoint-group-matches-list">
                                                {formData.patternInput && liveMatches.length > 0 ? (
                                                    liveMatches.map((device) => (
                                                        <div
                                                            key={`live-${device.id}`}
                                                            className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b text-xs last:border-b-0 hover:bg-muted/20"
                                                        >
                                                            <div className="col-span-10 font-medium break-all flex items-center gap-2">
                                                                {device.os.toLowerCase().includes("server") ? (
                                                                    <Monitor className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                ) : device.name.toLowerCase().includes("laptop") ||
                                                                  device.name.toLowerCase().includes("phany") ? (
                                                                    <Laptop className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                ) : (
                                                                    <Smartphone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                )}
                                                                <span className="truncate">{device.name}</span>
                                                            </div>
                                                            <div className="col-span-2 text-center text-muted-foreground italic">
                                                                -
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : selectedPattern ? (
                                                    formData.devicePatterns
                                                        .filter((pattern) => pattern.pattern === selectedPattern)
                                                        .flatMap((pattern) =>
                                                            pattern.includedDevices.map((device) => (
                                                                <div
                                                                    key={`${pattern.id}-${device.id}`}
                                                                    className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b text-xs last:border-b-0 hover:bg-muted/20"
                                                                >
                                                                    <div className="col-span-10 font-medium break-all flex items-center gap-2">
                                                                        {device.os.toLowerCase().includes("server") ? (
                                                                            <Monitor className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                        ) : device.name
                                                                              .toLowerCase()
                                                                              .includes("laptop") ||
                                                                          device.name
                                                                              .toLowerCase()
                                                                              .includes("phany") ? (
                                                                            <Laptop className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                        ) : (
                                                                            <Smartphone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                                                        )}
                                                                        <span className="truncate">{device.name}</span>
                                                                    </div>
                                                                    <div className="col-span-2">
                                                                        <Checkbox
                                                                            aria-label={`Include ${device.name}`}
                                                                            checked={pattern.includedDevices.some(
                                                                                (d) => d.id === device.id,
                                                                            )}
                                                                            onCheckedChange={() =>
                                                                                toggleDeviceInPattern(
                                                                                    pattern.id,
                                                                                    device,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )),
                                                        )
                                                ) : (
                                                    <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                                                        {formData.devicePatterns.length > 0
                                                            ? "Select a pattern to view its devices"
                                                            : "Add patterns or type to see matched devices"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <DialogFooter className="management-dialog-footer">
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                            disabled={isSaving}
                            data-testid="button-cancel"
                        >
                            Cancel
                        </Button>
                        <EndpointActionButton
                            onClick={async () => {
                                setIsSaving(true);
                                try {
                                    await handleCreateGroup();
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                            disabled={
                                isSaving ||
                                !formData.name.trim() ||
                                !formData.profile ||
                                (formData.criteria === "device_pattern" && formData.devicePatterns.length === 0)
                            }
                            data-testid="button-submit"
                        >
                            {isSaving && <LoaderCircle className="w-4 h-4 animate-spin" aria-hidden="true" />}
                            {isSaving ? "Saving..." : formData.isEdit ? "Save changes" : "Create group"}
                        </EndpointActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                open={showDeleteConfirmation}
                onOpenChange={setShowDeleteConfirmation}
                className="management-dialog management-confirmation"
                title="Delete Endpoint Group"
                description={`Are you sure, you want to delete this group - ${selectedGroupDetails?.name}?`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
                onConfirm={() => {
                    if (selectedGroupDetails) handleDelete(selectedGroupDetails);
                }}
            />
        </div>
    );
}
